import Tabs from "@/components/Tabs";
import { useFlags } from "@/hooks/useFeatureFlags";
import { useCanViewPersonContactBool, useCanViewPersonGroupsBool } from "@/hooks/useCapabilities";
import { Path, useNavigate } from "@/router";
import { useLocation } from "react-router-dom";
import { useParams } from "react-router-dom";

type TabOption = {
  label: string;
  to: Path;
  active: boolean;
};

export default function TabLayout({ children }: { children: React.ReactNode }) {
  const { pgaId } = useParams();

  const location = useLocation();
  const { pathname } = location;
  const currentUrl = pathname.replace(/\/$/, "");
  const navigate = useNavigate();
  const flags = useFlags();
  const canViewPersonContact = useCanViewPersonContactBool();
  const canViewPersonGroups = useCanViewPersonGroupsBool();

  if (!pgaId) return null;

  return (
    <>
      <Tabs
        tabItems={(
          [
            {
              label: "Overview",
              to: "/people/:pgaId/person/overview" as Path,
              active: currentUrl === `/people/${pgaId}/person/overview`
            },
            canViewPersonContact && {
              label: "Contact",
              to: "/people/:pgaId/person/contact" as Path,
              active: currentUrl === `/people/${pgaId}/person/contact`
            },
            {
              label: "Change Requests",
              to: "/people/:pgaId/person/change-requests" as Path,
              active: currentUrl === `/people/${pgaId}/person/change-requests`
            },
            {
              label: "Change History",
              to: "/people/:pgaId/person/change-history" as Path,
              active: currentUrl === `/people/${pgaId}/person/change-history`
            },
            flags["backoffice-person-profile-documents"] && {
              label: "Documents",
              to: "/people/:pgaId/person/documents" as Path,
              active: currentUrl === `/people/${pgaId}/person/documents`
            },
            canViewPersonGroups && {
              label: "Groups",
              to: "/people/:pgaId/person/groups" as Path,
              active: currentUrl === `/people/${pgaId}/person/groups`
            },
            flags["m360-timeline"] && {
              label: "Timeline",
              to: "/people/:pgaId/person/timeline" as Path,
              active: currentUrl === `/people/${pgaId}/person/timeline`
            },
            {
              label: "Notes",
              to: "/people/:pgaId/person/notes" as Path,
              active: currentUrl === `/people/${pgaId}/person/notes`
            }
          ] as Array<TabOption | false | undefined>
        )
          .filter((tab): tab is TabOption => !!tab)
          .map(({ label, to, active }) => ({
            label,
            onClick: () => navigate(to, { params: { pgaId } }),
            active
          }))}
      />
      <div className="flex flex-col min-w-0" data-testid="tab-content">
        {children}
      </div>
    </>
  );
}
